import { activeRequestsForFlag, askRestaurantOptions, askSourceForRestaurant, AskRestaurantOption, matchingFlagRestaurant, qtyFlagRestaurants, qtyFlagRowRequests } from './quantityFlagRestaurant';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { canUseSuperAdminFeatures } from '../../services/accessManagement/accessManagement';
import { getRestaurantsForProductOverride, FlaggedProductWithRestaurant, QtyFlagCustomerRequest } from '../../services/admin/adminService';
import { customerRequestsService } from '../../services/customerRequests/customerRequestsService';
import { outlinedActionButtonClass, borderPrimary, cardBg, primaryText, secondaryText } from '../../styling/stylingHelper';
import { ExpandModal } from '../ui/ExpandModal';
import { CustomerRequestComposer } from './CustomerRequestComposer';

// The flag's own list only covers what it could resolve when the page loaded; the dropdown must also block
// restaurants that came from the override lookup. A failed read falls back to the flag's list: the server
// still refuses a duplicate.
async function liveActiveRequests(flag: FlaggedProductWithRestaurant): Promise<QtyFlagCustomerRequest[]> {
  try {
    const pages = await Promise.all(['pending', 'awaiting_review'].map(status => customerRequestsService.list({ type: 'product_quantity', status, limit: 200 })));
    return activeRequestsForFlag(pages.flatMap(page => page.items), flag);
  } catch (err) {
    console.error('Failed to load active questions for quantity flag', err);
    return [];
  }
}

export function AskQuantityFlagButton({ flag, duplicates }: { flag: FlaggedProductWithRestaurant; duplicates?: FlaggedProductWithRestaurant[] }) {
  const { t } = useTranslation();
  const { backendUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AskRestaurantOption[]>([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  if (!canUseSuperAdminFeatures(backendUser)) return null;
  const flags = duplicates && duplicates.length > 0 ? duplicates : [flag];
  const known = flags.flatMap(qtyFlagRestaurants);
  const flagRequests = qtyFlagRowRequests(flags);
  const knownOptions = askRestaurantOptions(known, flagRequests);
  const allKnownActive = knownOptions.length > 0 && knownOptions.every(option => option.activeRequestId);
  const load = async () => {
    setOpen(true); setLoading(true); setError(''); setOptions([]); setSelected('');
    try {
      const [rows, live] = await Promise.all([known.length > 0 ? known : getRestaurantsForProductOverride(flag.platform, flag.articleId, flag.productName), liveActiveRequests(flag)]);
      const next = askRestaurantOptions(rows, [...flagRequests, ...live]);
      setOptions(next);
      const preselect = known.length > 0
        ? (next.length === 1 ? next[0].restaurantId : undefined)
        : matchingFlagRestaurant(next, flag.restaurantId);
      setSelected(next.find(option => option.restaurantId === preselect && !option.activeRequestId)?.restaurantId ?? '');
    } catch { setError(t('customerRequests.error')); }
    finally { setLoading(false); }
  };
  const allActive = options.length > 0 && options.every(option => option.activeRequestId);
  return <>
    <button type="button" disabled={allKnownActive} title={allKnownActive ? t('customerRequests.askAllActive') : undefined} className={`${outlinedActionButtonClass} disabled:opacity-50`} onClick={event => { event.stopPropagation(); void load(); }}>{t('customerRequests.ask')}</button>
    {open && selected && !loading && createPortal(<CustomerRequestComposer key={selected} seed={{ restaurantId: selected, type: 'product_quantity', productName: flag.productName, platform: flag.platform, source: askSourceForRestaurant(flags, selected) }} onClose={() => setOpen(false)} />, document.body)}
    {open && (!selected || loading) && createPortal(<ExpandModal isOpen onClose={() => setOpen(false)} title={t('customerRequests.ask')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <p className={primaryText}>{flag.productName} · {flag.platform}</p>
        {loading ? <p role="status">{t('customerRequests.loading')}</p> : allActive ? <div className="space-y-1 text-sm">
          <p className={secondaryText}>{t('customerRequests.askAllActive')}</p>
          {options.map(option => <Link key={option.restaurantId} className={`block underline ${secondaryText}`} to={`/admin/communication/customer-requests?requestId=${encodeURIComponent(option.activeRequestId ?? '')}`}>{option.restaurantName}</Link>)}
        </div> : <label className={`block text-sm ${primaryText}`}>
          {t('customerRequests.restaurant')}
          <select disabled={Boolean(flag.setupSource) && options.length === 1} className={`mt-1 w-full rounded-lg border p-2 ${borderPrimary} ${cardBg} ${primaryText}`} value={selected} onChange={event => setSelected(event.target.value)}>
            <option value="">{t('customerRequests.chooseRestaurant')}</option>
            {options.map(option => <option key={option.restaurantId} value={option.restaurantId} disabled={Boolean(option.activeRequestId)}>{option.activeRequestId ? t('customerRequests.askOptionActive', { restaurant: option.restaurantName }) : option.restaurantName}</option>)}
          </select>
        </label>}
        {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p>}
      </div>
    </ExpandModal>, document.body)}
  </>;
}
